import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { type FC, useState } from "react";
import { Link } from "react-router-dom";

import { Small } from "@fronterahealth/frontera-ui-components";

import { type RulePackage, type RulePackageRule } from "@api/rest/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { PackageDetailsForm } from "@pages/QARules/PackageDetailsForm";
import { PackageRuleItem } from "@pages/QARules/PackageRuleItem";
import { KebabMenu, type MenuItem, RULE_GRID } from "@pages/QARules/RuleEditorParts";
import { StillBadge } from "@pages/QARules/StillBadge";
import { PackageType, ReportCategory } from "@pages/QARules/constants";
import {
  type PackageRuleFields,
  type RuleChip,
  changedFields,
  draftAfterSave,
  fieldsOf,
  ruleInputFrom,
  toggleChip,
} from "@pages/QARules/qaRuleFields";
import {
  PACKAGE_TYPE_BADGE,
  REPORT_CATEGORY_BADGE,
  packageHref,
  packageToJson,
  packageTypeOf,
  reportCategoryOf,
} from "@pages/QARules/qaRuleLabels";
import { useQARuleApi } from "@pages/QARules/useQARuleApi";

interface PackageCardProps {
  pkg: RulePackage;
  payorTypes: string[];
  defaultExpanded?: boolean;
  /** Show only this rule (expanded and outlined), with a toggle to show the rest. */
  focusedRuleId?: number;
  /** Make the package name a link to its page (on the package list). */
  linkToDetail?: boolean;
  /**
   * Edited from an org or template page: hide changes that reach beyond that
   * org / template (package type, duplicate, archive).
   */
  restricted?: boolean;
}

/**
 * One QA package: header with type / category / payor badges and attachment counts,
 * an inline details editor, and its ordered rules (plus any archived ones, if loaded).
 */
export const PackageCard: FC<PackageCardProps> = ({
  pkg,
  payorTypes,
  defaultExpanded,
  focusedRuleId,
  linkToDetail,
  restricted,
}) => {
  const qaRuleApi = useQARuleApi();
  const [expanded, setExpanded] = useState(defaultExpanded ?? false);
  const [editing, setEditing] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [expandedRuleIds, setExpandedRuleIds] = useState<Set<number>>(
    () => new Set(focusedRuleId ? [focusedRuleId] : []),
  );
  const [showAllRules, setShowAllRules] = useState(!focusedRuleId);
  const [showArchivedRules, setShowArchivedRules] = useState(false);
  // Local edits not yet saved, per rule; cleared once a save lands.
  const [drafts, setDrafts] = useState<Record<number, Partial<PackageRuleFields>>>({});

  const rules = pkg.rules.filter((rule) => !rule.archived_at);
  const archivedRules = pkg.rules.filter((rule) => rule.archived_at);
  const shownRules = showAllRules ? rules : rules.filter((rule) => rule.id === focusedRuleId);
  const isArchived = !!pkg.archived_at;
  const category = reportCategoryOf(pkg.report_types);
  const typeBadge = PACKAGE_TYPE_BADGE[packageTypeOf(pkg.package_type)];
  const categoryBadge = REPORT_CATEGORY_BADGE[category];

  const withErrorToast = async (action: () => Promise<unknown>): Promise<boolean> => {
    try {
      await action();
      return true;
    } catch (e) {
      notifyError(e instanceof Error ? e.message : "Something went wrong.");
      return false;
    }
  };

  // ---- Rule editing ----

  const draftFor = (rule: RulePackageRule): PackageRuleFields => ({ ...fieldsOf(rule), ...(drafts[rule.id] ?? {}) });
  const setDraft = (rule: RulePackageRule, patch: Partial<PackageRuleFields>) =>
    setDrafts((d) => ({ ...d, [rule.id]: { ...(d[rule.id] ?? {}), ...patch } }));
  const clearSaved = (rule: RulePackageRule, saved: Partial<PackageRuleFields>) =>
    setDrafts((d) => ({ ...d, [rule.id]: draftAfterSave(d[rule.id] ?? {}, saved) }));

  // Sends only what changed, so overlapping saves of different fields (a blur, then a
  // chip click) can't put back each other's old values.
  const save = async (rule: RulePackageRule, fields: PackageRuleFields) => {
    const changed = changedFields(fields, fieldsOf(rule));
    // Blurring an unchanged field must not fire a write.
    if (Object.keys(changed).length === 0) return;
    const saved = await withErrorToast(() => qaRuleApi.updateRule({ id: rule.id, ...ruleInputFrom(changed) }));
    if (saved) clearSaved(rule, changed);
  };

  const savePatch = (rule: RulePackageRule, patch: Partial<PackageRuleFields>) => {
    setDraft(rule, patch);
    void save(rule, { ...draftFor(rule), ...patch });
  };

  const toggleRuleChip = (rule: RulePackageRule, chip: RuleChip) => {
    const current = draftFor(rule).chips;
    const next = toggleChip(current, chip);
    if (next !== current) savePatch(rule, { chips: next });
  };

  // A new rule has nothing to scan for yet, so it isn't sent until it's filled in.
  const addRule = () =>
    withErrorToast(async () => {
      const result = await qaRuleApi.createRule({ rule_set_id: pkg.id, name: "" });
      if (result.status === 201) setExpandedRuleIds((s) => new Set(s).add(result.data.id));
    });

  const move = async (index: number, offset: number) => {
    const ids = rules.map((r) => r.id);
    [ids[index], ids[index + offset]] = [ids[index + offset], ids[index]];
    setReordering(true);
    await withErrorToast(() => qaRuleApi.reorderRules(pkg.id, ids));
    setReordering(false);
  };

  const toggleRuleExpand = (id: number) =>
    setExpandedRuleIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allExpanded = rules.length > 0 && rules.every((r) => expandedRuleIds.has(r.id));

  const exportJson = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(packageToJson(pkg), null, 2));
      notifySuccess("Copied package JSON to clipboard");
    } catch {
      notifyError("Couldn't copy to the clipboard.");
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: "Edit package details",
      onClick: () => {
        setEditing(true);
        setExpanded(true);
      },
    },
    ...(restricted
      ? []
      : [
          { label: "Duplicate", onClick: () => void withErrorToast(() => qaRuleApi.duplicatePackage(pkg.id)) },
          { label: "Export JSON", onClick: () => void exportJson() },
          {
            label: isArchived ? "Restore" : "Archive",
            onClick: () => void withErrorToast(() => qaRuleApi.updatePackage({ id: pkg.id, archived: !isArchived })),
            danger: !isArchived,
          },
        ]),
  ];

  return (
    <div
      id={`qa-package-${pkg.id}`}
      className={clsx(
        "w-full rounded-lg border border-border-subtle shadow-sm",
        isArchived ? "bg-bg-default" : "bg-bg-surface",
      )}
    >
      <div className="flex items-start gap-3 px-4 py-4">
        <button
          type="button"
          className="mt-0.5 flex-shrink-0 text-text-tertiary hover:text-text-primary"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Collapse package" : "Expand package"}
        >
          {expanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {linkToDetail ? (
              <Link
                to={packageHref(pkg.id)}
                className={clsx(
                  "font-semibold hover:underline",
                  isArchived ? "text-text-tertiary" : "text-text-primary",
                )}
              >
                {pkg.name}
              </Link>
            ) : (
              <span className={clsx("font-semibold", isArchived ? "text-text-tertiary" : "text-text-primary")}>
                {pkg.name}
              </span>
            )}
            <StillBadge text={typeBadge.label} appearance={typeBadge.appearance} />
            {categoryBadge && <StillBadge text={categoryBadge.label} appearance={categoryBadge.appearance} />}
            {isArchived && <StillBadge text="Archived" appearance="error" />}
            {pkg.payor_types.map((value) => (
              <StillBadge key={value} text={value} appearance="neutral" />
            ))}
            <Small colorType="secondary">
              {rules.length} rule{rules.length === 1 ? "" : "s"}
              {packageTypeOf(pkg.package_type) === PackageType.CUSTOM &&
                ` · attached to ${pkg.org_count} org${pkg.org_count === 1 ? "" : "s"}, ${pkg.template_count} template${
                  pkg.template_count === 1 ? "" : "s"
                }`}
            </Small>
          </div>
          {pkg.description && (
            <Small colorType="secondary" className="mt-0.5 block">
              {pkg.description}
            </Small>
          )}
        </div>
        <KebabMenu ariaLabel="Package actions" items={menuItems} />
      </div>

      {expanded && (
        <div className="border-t border-border-subtle">
          {editing ? (
            <div className="m-4">
              <PackageDetailsForm
                idPrefix={`edit-package-${pkg.id}`}
                initial={pkg}
                payorTypes={payorTypes}
                submitText="Save"
                isPending={savingDetails}
                lockPackageType={restricted}
                onCancel={() => setEditing(false)}
                onSubmit={async (fields) => {
                  setSavingDetails(true);
                  const saved = await withErrorToast(() => qaRuleApi.updatePackage({ id: pkg.id, ...fields }));
                  setSavingDetails(false);
                  if (saved) setEditing(false);
                }}
              />
            </div>
          ) : (
            pkg.instructions && (
              <div className="m-4">
                <p className="mb-1 text-xs font-medium text-text-secondary">Instructions</p>
                <p className="whitespace-pre-line text-sm text-text-primary">{pkg.instructions}</p>
              </div>
            )
          )}

          {rules.length > 0 && (
            <>
              <div className="flex items-center justify-end border-t border-border-subtle px-4 py-1.5 pr-6">
                <button
                  type="button"
                  onClick={() => setExpandedRuleIds(allExpanded ? new Set() : new Set(rules.map((r) => r.id)))}
                  className="text-xs font-medium text-globemallow-700 hover:underline"
                >
                  {allExpanded ? "Collapse all" : "Expand all"}
                </button>
              </div>
              <div
                className={clsx(
                  RULE_GRID,
                  "border-t border-border-subtle bg-bg-default px-4 py-2 pl-6 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary",
                )}
              >
                <div>Rule name</div>
                <div>{category === ReportCategory.DIAGNOSIS ? "" : "Report type"}</div>
                <div>Location in report</div>
                <div />
              </div>
            </>
          )}

          {shownRules.map((rule) => {
            const index = rules.indexOf(rule);
            const canMove = !reordering && showAllRules;
            return (
              <PackageRuleItem
                key={rule.id}
                fields={draftFor(rule)}
                expanded={expandedRuleIds.has(rule.id)}
                highlighted={rule.id === focusedRuleId}
                showChips={category !== ReportCategory.DIAGNOSIS}
                handlers={{
                  onToggleExpand: () => toggleRuleExpand(rule.id),
                  onChangeField: (patch) => setDraft(rule, patch),
                  onSavePatch: (patch) => savePatch(rule, patch),
                  onToggleChip: (chip) => toggleRuleChip(rule, chip),
                  onBlurSave: () => void save(rule, draftFor(rule)),
                  onArchive: () => void withErrorToast(() => qaRuleApi.updateRule({ id: rule.id, archived: true })),
                  onMoveUp: canMove && index > 0 ? () => void move(index, -1) : undefined,
                  onMoveDown: canMove && index < rules.length - 1 ? () => void move(index, 1) : undefined,
                }}
              />
            );
          })}

          {!showAllRules && rules.length > shownRules.length && (
            <div className="border-t border-border-subtle px-4 py-2 pl-6">
              <button
                type="button"
                onClick={() => setShowAllRules(true)}
                className="text-xs font-medium text-globemallow-700 hover:underline"
              >
                Show all {rules.length} rules in this package
              </button>
            </div>
          )}

          {rules.length === 0 && <p className="px-4 py-3 text-xs text-text-tertiary">No rules yet.</p>}

          <div className="flex items-center justify-between border-t border-border-subtle px-4 py-2 pl-6">
            <button
              type="button"
              onClick={() => void addRule()}
              className="flex items-center gap-1 rounded px-2 py-1 text-sm font-medium text-globemallow-700 hover:bg-globemallow-50"
            >
              + Add rule
            </button>
            {archivedRules.length > 0 && (
              <button
                type="button"
                onClick={() => setShowArchivedRules((v) => !v)}
                className="text-xs font-medium text-text-secondary hover:underline"
              >
                {showArchivedRules ? "Hide" : "Show"} {archivedRules.length} archived rule
                {archivedRules.length === 1 ? "" : "s"}
              </button>
            )}
          </div>

          {showArchivedRules &&
            archivedRules.map((rule) => (
              <div
                key={rule.id}
                className="flex items-center justify-between border-t border-border-subtle bg-bg-default px-4 py-2 pl-6"
              >
                <span className="truncate text-sm text-text-tertiary line-through">{rule.name || "Untitled rule"}</span>
                <button
                  type="button"
                  onClick={() => void withErrorToast(() => qaRuleApi.updateRule({ id: rule.id, archived: false }))}
                  className="text-xs font-medium text-globemallow-700 hover:underline"
                >
                  Restore
                </button>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
